import { useState, useEffect, useCallback } from 'react';
import { User } from 'firebase/auth';
import { auth, signInAnonymous, onAuthStateChange, isFirebaseConfigured } from '@/lib/firebase';
import { getAuthHeaders } from '@/lib/queryClient';
import { supabaseHelpers } from '@/lib/supabase';
import { stateMigration, type MigrationResult } from '@/lib/state-migration';
import { apiUrl } from '@/lib/config';

const isDev = import.meta.env.DEV;
const debug = (...args: unknown[]) => { if (isDev) console.log(...args); };

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

interface SessionInfo {
  lastSyncedUser: string | null;
  appVersion: string | null;
  lastMigration: string | null;
  firebaseConfigured: boolean;
}

// Module-level variable for throttling (outside hook)
const globalLastSyncTime = { current: 0 };

export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  });

  const [isInitialized, setIsInitialized] = useState(false);
  const [migrationCompleted, setMigrationCompleted] = useState(false);
  const [migrationResult, setMigrationResult] = useState<MigrationResult | null>(null);

  const updateAuthState = useCallback((updates: Partial<AuthState>) => {
    setAuthState(prev => ({ ...prev, ...updates }));
  }, []);


  // ...

  const syncUserWithBackend = useCallback(async (user: User) => {
    const now = Date.now();
    // Throttle syncs to once every 30 seconds to prevent loops/spam
    if (now - globalLastSyncTime.current < 30000) {
      debug('Skipping user sync - synced recently (' + Math.round((now - globalLastSyncTime.current) / 1000) + 's ago)');
      return;
    }

    globalLastSyncTime.current = now;

    try {
      debug('Syncing user with backend:', user.uid);
      // The "real" display name we know about — either from Firebase
      // (rare for anon/email-link users) or from a prior onboarding on
      // this browser. May be null on a fresh browser logging in to an
      // existing account; in that case we MUST NOT send a placeholder
      // because the server's PATCH would overwrite the real name stored
      // in the DB. Let the server preserve the existing display_name.
      const knownDisplayName =
        user.displayName ||
        localStorage.getItem('vyzle-chat-user-name') ||
        null;
      // Generated placeholder — only used when we need to upsert a brand
      // new user row (server's PATCH-with-no-existing-row path requires
      // displayName to create). Never sent for existing users.
      const placeholderDisplayName = `Vyzle_${user.uid.slice(-4).toUpperCase()}`;

      const patchBody: Record<string, unknown> = {
        email: user.email,
        photoURL: user.photoURL,
        preferredLanguage: localStorage.getItem('vyzle-chat-language') || 'en',
        isAnonymous: user.isAnonymous,
        ...(user.email && !user.isAnonymous ? { authProvider: 'email-link' } : {}),
      };
      if (knownDisplayName) {
        // Only send when we actually have a real value — prevents
        // overwriting an existing display_name for returning users
        // signing in on a fresh browser.
        patchBody.displayName = knownDisplayName;
      }

      const authHeaders = await getAuthHeaders();
      const res = await fetch(apiUrl(`/api/users/${user.uid}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(patchBody),
      });

      // 404 means the user doesn't exist yet AND we didn't send a
      // displayName for upsert. Retry with the placeholder so we at
      // least have a row.
      if (res.status === 404 && !knownDisplayName) {
        await fetch(apiUrl(`/api/users/${user.uid}`), {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({ ...patchBody, displayName: placeholderDisplayName }),
        });
      }

      // Keep localStorage in sync with the resolved name
      const resolvedName = knownDisplayName || placeholderDisplayName;
      if (!localStorage.getItem('vyzle-chat-user-name')) {
        localStorage.setItem('vyzle-chat-user-name', resolvedName);
      }
      localStorage.setItem('vyzle-chat-last-synced-user', user.uid);
      localStorage.setItem('vyzle-chat-last-sync-time', new Date().toISOString());
      debug('User synced to backend successfully:', user.uid);
    } catch (error) {
      console.warn('Failed to sync user with backend:', error);
    }
  }, []);

  // Try to get existing user from Supabase
  useEffect(() => {
    const checkUser = async () => {
      if (!authState.user?.uid) return;
      
      const existingUser = await supabaseHelpers.getUser(authState.user.uid);
      debug('Existing user check result:', !!existingUser);

      if (!existingUser) {
        // Create new user in Supabase
        const userName = localStorage.getItem('vyzle-chat-user-name') || `Vyzle_${authState.user.uid.slice(-4).toUpperCase()}`;
        const userLanguage = localStorage.getItem('vyzle-chat-language') || 'en';

        debug('Creating new user in Supabase:', { userName, userLanguage });

        const newUser = await supabaseHelpers.createUser({
          uid: authState.user.uid,
          display_name: userName,
          preferred_language: userLanguage,
        });

        debug('User created in Supabase successfully:', !!newUser);
      } else {
        // Update existing user's online status AND profile matching localStorage
        debug('Updating existing user profile and status in Supabase');

        const userName = localStorage.getItem('vyzle-chat-user-name');
        const userLanguage = localStorage.getItem('vyzle-chat-language');

        const updates: any = {
          is_online: true,
          last_seen: new Date().toISOString(),
        };

        if (userName) updates.display_name = userName;
        if (userLanguage) updates.preferred_language = userLanguage;

        await supabaseHelpers.updateUser(authState.user.uid, updates);

        debug('User profile updated in Supabase:', authState.user.uid);
      }
    };
    
    if (authState.user) {
      checkUser();
    }
  }, [authState.user]);


const signIn = useCallback(async () => {
  debug('signIn function called');
  try {
    updateAuthState({ loading: true, error: null });
    const result = await signInAnonymous();
    debug('signInAnonymous successful:', result.user.uid);

    await syncUserWithBackend(result.user);

    updateAuthState({ user: result.user, loading: false });
    return result.user;
  } catch (error: any) {
    console.error('Sign in error:', error);
    updateAuthState({
      error: error.message || 'Failed to sign in',
      loading: false
    });
    throw error;
  }
}, [updateAuthState, syncUserWithBackend]);

const signOut = useCallback(async () => {
  try {
    // Update user's online status in Supabase before signing out
    if (authState.user?.uid) {
      try {
        await supabaseHelpers.updateUser(authState.user.uid, {
          is_online: false,
          last_seen: new Date().toISOString(),
        });
      } catch (error) {
        console.warn('Failed to update user status on sign out:', error);
      }
    }

    // Use Firebase auth signOut if available, otherwise just clear local state
    if (auth) {
      await auth.signOut();
    }
    updateAuthState({ user: null, error: null });
  } catch (error: any) {
    console.error('Sign out error:', error);
    updateAuthState({ error: error.message || 'Failed to sign out' });
  }
}, [authState.user, updateAuthState]);

const retryAuth = useCallback(async () => {
  try {
    updateAuthState({ loading: true, error: null });

    // Clear any existing auth state
    if (auth) {
      await auth.signOut();
    }

    // Clear session storage to force re-auth
    sessionStorage.removeItem('vyzle-chat-auth-triggered');

    // Retry sign in
    await signIn();
  } catch (error: any) {
    console.error('Retry auth failed:', error);
    updateAuthState({
      error: error.message || 'Failed to retry authentication',
      loading: false
    });
  }
}, [updateAuthState, signIn]);

const getSessionInfo = useCallback((): SessionInfo => {
  return {
    lastSyncedUser: localStorage.getItem('vyzle-chat-last-synced-user'),
    appVersion: localStorage.getItem('vyzle-chat-app-version'),
    lastMigration: localStorage.getItem('vyzle-chat-last-migration'),
    firebaseConfigured: isFirebaseConfigured(),
  };
}, []);

    // Initialize app state and run migrations
useEffect(() => {
  const initializeApp = async () => {
    try {
      debug('Initializing app state...');

      // Run state migration
      const migration = await stateMigration.migrate();
      setMigrationResult(migration);

      if (migration.success) {
        debug('State migration completed successfully');
        setMigrationCompleted(true);
      } else {
        console.warn('State migration completed with errors:', migration.errors);
        setMigrationCompleted(true); // Still mark as completed to allow app to function
      }

      setIsInitialized(true);
    } catch (error) {
      console.error('Failed to initialize app state:', error);
      setMigrationCompleted(true); // Mark as completed to prevent infinite loading
      setIsInitialized(true);
    }
  };

  initializeApp();
}, []);

// Set up auth state listener after initialization
useEffect(() => {
  if (!isInitialized || !migrationCompleted) {
    return;
  }

  const unsubscribe = onAuthStateChange(async (user) => {
    debug('Auth state change detected:', { authenticated: !!user });

    if (user) {
      try {
        await syncUserWithBackend(user);
        updateAuthState({ user, loading: false, error: null });
      } catch (error) {
        console.error('Failed to sync user on auth state change:', error);
        updateAuthState({ user, loading: false, error: null });
      }
    } else {
      // Only auto-sign-in if the user has already completed onboarding (name is known).
      // New users must go through the WelcomeFlow first — it calls signIn() after the
      // name is collected, so we never PATCH the backend with a null displayName.
      const isOnboarded =
        !!localStorage.getItem('vyzle-chat-onboarded') &&
        !!localStorage.getItem('vyzle-chat-user-name');

      if (isFirebaseConfigured() && isOnboarded) {
        debug('Returning user — auto-signing in...');
        try {
          await signIn();
        } catch (error) {
          console.error('Automatic sign-in failed:', error);
          updateAuthState({ user: null, loading: false, error: null });
        }
      } else {
        // Not onboarded yet — let WelcomeFlow handle sign-in after name is entered
        debug('Onboarding not completed — deferring sign-in to WelcomeFlow');
        updateAuthState({ user: null, loading: false, error: null });
      }
    }
  });

  return () => {
    if (typeof unsubscribe === 'function') {
      unsubscribe();
    }
  };
}, [isInitialized, migrationCompleted, updateAuthState, syncUserWithBackend, signIn]);

// Listen for socket connection to re-sync user (critical for translation after server restart)
useEffect(() => {
  const handleSocketConnect = async (e: Event) => {
    const detail = (e as CustomEvent).detail;
    if (authState.user && detail && detail.userId === authState.user.uid) {
      debug('Socket connected, re-syncing user profile to server...');
      await syncUserWithBackend(authState.user);
    }
  };

  window.addEventListener('vyzle-chat-socket-connected', handleSocketConnect);

  return () => {
    window.removeEventListener('vyzle-chat-socket-connected', handleSocketConnect);
  };
}, [authState.user, syncUserWithBackend]);

return {
  user: authState.user,
  loading: authState.loading,
  error: authState.error,
  signIn,
  signOut,
  retryAuth,
  isFirebaseConfigured: isFirebaseConfigured(),
  isInitialized,
  migrationCompleted,
  getSessionInfo,
  authError: authState.error, // Alias for compatibility
};
}
